import i18n from '../i18n';
import { categories } from './categories';
import { getMacroName, getMicroName } from './getCategoryData';
import { UTILITIES } from './utilities';

const getCategorySuggestions = (query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results = [];

  categories.forEach((cat) => {
    const macroLabel = getMacroName(cat.id);
    if (macroLabel.toLowerCase().includes(q)) {
      results.push({
        name: macroLabel,
        type: 'category',
        macroId: cat.id,
      });
    }

    cat.micro?.forEach((micro) => {
      const microLabel = getMicroName(micro.id);
      if (microLabel.toLowerCase().includes(q)) {
        results.push({
          name: microLabel,
          type: 'subcategory',
          macroId: cat.id,
          microId: micro.id,
        });
      }
    });
  });

  // Match utilities by labels and synonyms in all languages
  const lang = (i18n.language || 'en').startsWith('pt') ? 'pt' : 'en';

  UTILITIES.forEach((u) => {
    const matches =
      Object.values(u.labels).some((l) => l.toLowerCase().includes(q)) ||
      Object.values(u.synonyms || {}).some((arr) =>
        arr.some((s) => s.toLowerCase().includes(q))
      );

    if (matches) {
      results.push({
        name: u.labels[lang] || u.labels.en,
        type: 'utility',
        utilityId: u.id,
      });
    }
  });

  return results;
};

// Derive category/utility ids from the free-text query, shared by the
// homepage HeroSearch and the listings EventSearchBar so both surfaces send
// identical search params. A subcategory match maps ONLY to its micro id —
// adding the parent macro here would broaden results to the whole parent
// category (e.g. "Footwear" returning all Fashion events).
export const getIdsFromQuery = (q) => {
  const raw = (q || '').trim();
  if (raw.length < 2) return { qMacroIds: [], qMicroIds: [], qUtilityIds: [] };

  const suggs = getCategorySuggestions(raw) || [];

  const macro = new Set();
  const micro = new Set();
  const utilities = new Set();

  for (const s of suggs) {
    if (s.type === 'category' && s.macroId != null) {
      macro.add(parseInt(s.macroId, 10));
    }
    if (s.type === 'subcategory' && s.microId != null) {
      micro.add(parseInt(s.microId, 10));
    }
    if (s.type === 'utility' && s.utilityId) {
      utilities.add(s.utilityId);
    }
  }

  return {
    qMacroIds: Array.from(macro).filter((n) => Number.isFinite(n)),
    qMicroIds: Array.from(micro).filter((n) => Number.isFinite(n)),
    qUtilityIds: Array.from(utilities),
  };
};

export default getCategorySuggestions;
